import { normalizeScheduleFromApi } from './scheduleDisplay';
import { CLINIC_TIMEZONE } from './clinicTime';

const DAY_NAMES_LOWER = {
  1: 'lunes',
  2: 'martes',
  3: 'miércoles',
  4: 'jueves',
  5: 'viernes',
  6: 'sábado',
  7: 'domingo',
};

const WEEKDAY_TO_ISO = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

function toMinutes(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Día ISO (1=lunes) y minutos actuales en la zona horaria de la tienda. */
export function nowInStore(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CLINIC_TIMEZONE,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const isoWeekday = WEEKDAY_TO_ISO[get('weekday')] || 1;
  const minutes = (Number(get('hour')) % 24) * 60 + Number(get('minute'));
  return { isoWeekday, minutes };
}

/**
 * Próxima apertura según GET /schedule (misma regla que el backend: horario por día y hora de comida).
 * @returns {{ dayOffset: number, isoWeekday: number, time: string } | null}
 */
export function nextOpening(scheduleRaw, { skipToday = false, date } = {}) {
  if (!scheduleRaw) return null;
  const s = normalizeScheduleFromApi(scheduleRaw);
  const working = new Set((s.working_days || []).map(Number));
  if (!working.size) return null;
  const { isoWeekday, minutes } = nowInStore(date);

  if (!skipToday && working.has(isoWeekday)) {
    const pd = s.per_day[String(isoWeekday)];
    const open = toMinutes(pd.opening_time);
    const close = toMinutes(pd.closing_time);
    const ls = toMinutes(pd.lunch_start);
    const le = toMinutes(pd.lunch_end);
    if (open != null && minutes < open) {
      return { dayOffset: 0, isoWeekday, time: pd.opening_time };
    }
    if (ls != null && le != null && le > ls && minutes >= ls && minutes < le && (close == null || le < close)) {
      return { dayOffset: 0, isoWeekday, time: pd.lunch_end };
    }
  }
  for (let off = 1; off <= 7; off++) {
    const wd = ((isoWeekday - 1 + off) % 7) + 1;
    if (working.has(wd)) {
      return { dayOffset: off, isoWeekday: wd, time: s.per_day[String(wd)].opening_time };
    }
  }
  return null;
}

/** «hoy», «mañana» o «el viernes». */
function dayPhrase(next) {
  if (next.dayOffset === 0) return 'hoy';
  if (next.dayOffset === 1) return 'mañana';
  return `el ${DAY_NAMES_LOWER[next.isoWeekday]}`;
}

/** true si la tienda NO está tomando pedidos ahora (fuera de horario o pausada). */
export function isStoreClosed(status) {
  return Boolean(status && status.open_now === false);
}

/**
 * Aviso corto para el cliente: «Estamos cerrados. Abrimos mañana a las 09:00.»
 * Si el admin pausó pedidos manualmente, se muestra su mensaje (pausa ≠ horario).
 */
export function closedNoticeText(status, scheduleRaw) {
  if (!isStoreClosed(status)) return '';
  if (status.paused || status.accepting_orders === false) {
    return String(status.message || '').trim() || 'Por el momento no estamos tomando pedidos.';
  }
  const skipToday = /excepci[oó]n/i.test(String(status.message || ''));
  const next = nextOpening(scheduleRaw, { skipToday });
  if (!next) return 'Estamos cerrados en este momento.';
  return `Estamos cerrados. Abrimos ${dayPhrase(next)} a las ${next.time}.`;
}

/** Etiqueta compacta para el botón de pagar cuando está cerrado. */
export function closedButtonLabel(status, scheduleRaw) {
  if (!isStoreClosed(status)) return '';
  if (status.paused || status.accepting_orders === false) return 'Pedidos en pausa';
  const skipToday = /excepci[oó]n/i.test(String(status.message || ''));
  const next = nextOpening(scheduleRaw, { skipToday });
  if (!next) return 'Cerrado por ahora';
  return `Cerrado · abrimos ${dayPhrase(next)} ${next.time}`;
}
